import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { createElement, type ReactNode } from 'react';

import { defineFieldReadModule, type FieldReadModule } from '../field-read-module.ts';
import {
  EMPTY_SELECT_CONTENT,
  type SelectReadContent,
  type SelectOptionContent
} from './select-read-content.ts';
import { SelectValueView } from './select-value-view.tsx';

export type SelectReadRenderer = (content: SelectReadContent) => ReactNode;

type CompiledSelectOptions = {
  readonly ordered: readonly SelectOptionContent[];
  readonly byStoredKey: ReadonlyMap<string, SelectReadContent>;
  readonly keyByLabel: ReadonlyMap<string, string>;
};

function compileOptions(field: DatabaseField): CompiledSelectOptions {
  const ordered: SelectOptionContent[] = [];
  const optionsByKey = new Map<string, SelectOptionContent[]>();
  const keyByLabel = new Map<string, string>();

  field.settings.selectOptions.forEach((option) => {
    const key = option.id ?? option.label;
    const content = Object.freeze({ key, label: option.label, color: option.color });

    ordered.push(content);

    const existing = optionsByKey.get(key);

    if (existing) {
      existing.push(content);
    } else {
      optionsByKey.set(key, [content]);
    }

    if (!keyByLabel.has(option.label.trim())) {
      keyByLabel.set(option.label.trim(), key);
    }
  });

  return Object.freeze({
    ordered: Object.freeze(ordered),
    byStoredKey: new Map(
      [...optionsByKey].map(([key, options]) => [
        key,
        Object.freeze<SelectReadContent>({
          kind: 'select',
          options: Object.freeze(options)
        })
      ])
    ),
    keyByLabel
  });
}

function parseTextLabels(value: string): readonly string[] {
  const trimmed = value.trim();

  if (!trimmed) {
    return [];
  }

  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return [trimmed.slice(1, -1)];
  }

  return trimmed.split(/,\s*/);
}

function resolveOptions(
  keys: readonly string[],
  options: CompiledSelectOptions
): SelectReadContent {
  if (keys.length === 0) {
    return EMPTY_SELECT_CONTENT;
  }

  if (keys.length === 1) {
    return options.byStoredKey.get(keys[0]) ?? EMPTY_SELECT_CONTENT;
  }

  const selectedKeys = new Set(keys);
  const selected = options.ordered.filter((option) => selectedKeys.has(option.key));

  if (selected.length === 0) {
    return EMPTY_SELECT_CONTENT;
  }

  return Object.freeze({ kind: 'select', options: Object.freeze(selected) });
}

const renderSelectValue: SelectReadRenderer = (content) =>
  createElement(SelectValueView, { content });

/**
 * Pairs the shared Select semantics with a consumer's typed presenter at composition.
 * Overrides cannot change value resolution or introduce layout policy into the reader.
 */
export function createSelectFieldReadModule({
  render = renderSelectValue
}: { readonly render?: SelectReadRenderer } = {}): FieldReadModule<SelectReadContent> {
  return defineFieldReadModule<SelectReadContent>({
    fieldType: DatabaseFieldType.SELECT,
    render,
    compile(field) {
      const options = compileOptions(field);

      return Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ): SelectReadContent {
          /**
           * V1 reads stored defaults for absent fields. defaultEnabled only controls
           * initialization of new entries; it must not hide existing sparse values.
           */
          const value = storedValue ?? defaultValue;

          if (value?.type === DatabaseFieldType.SELECT) {
            return resolveOptions(value.value, options);
          }

          if (value?.type === DatabaseFieldType.TEXT) {
            const keys = parseTextLabels(value.value.plain).map(
              (label) => options.keyByLabel.get(label) ?? label
            );

            return resolveOptions(keys, options);
          }

          return EMPTY_SELECT_CONTENT;
        }
      });
    }
  });
}

export const selectFieldReadModule = createSelectFieldReadModule();
